import { useMemo } from 'react';
import type { PartialUtmParameters } from '@shared/utils/utmParameters';
import { mergeUtmParameters, useUtmParameters } from '@shared/utils/utmParameters';

/**
 * Hook that merges current URL UTM parameters with internal/default UTM parameters.
 * Current URL UTM parameters take precedence over internal parameters to preserve
 * campaign/ads tracking.
 *
 * @param internalUtm - Internal/default UTM parameters to use as fallback
 * @returns Merged UTM parameters (current URL UTM + internal UTM with current taking precedence)
 *
 * @example
 * ```typescript
 * const internalUtm = { utm_source: 'hotel_search', utm_medium: 'web', utm_campaign: 'guest_map' };
 * const mergedUtm = useMergedUtmParameters(internalUtm);
 * // If URL has ?utm_source=google_ads, mergedUtm will have utm_source='google_ads'
 * ```
 */
export const useMergedUtmParameters = (internalUtm?: PartialUtmParameters): PartialUtmParameters => {
  const currentUtm = useUtmParameters();

  return useMemo(() => {
    // Merge UTM params - current URL UTM takes precedence over internal
    return mergeUtmParameters(currentUtm, internalUtm);
  }, [currentUtm, internalUtm]);
};
